import Mirador from '@mdz/mirador/dist/es/src/index'

import { calculatePageParamFromCanvases } from './helpers'
import plugins from './plugins'

interface CanvasLinkParams {
  canvases: any[]
  manifestId: string
  visibleCanvases: { id: string }[]
  windowViewType: string
}

interface CanvasNavigationParams {
  canvasId?: string
  canvasLabel: string
}

interface RenderViewerParams {
  dfgViewerUrl: string
  issueId: string
  language: string
  lastSearchUrl: string
  manifestUrl: string
  multId: string
  navDate: string
  target: string
}

/** Get the link for the given canvases */
const getCanvasLink = ({
  canvases,
  manifestId,
  visibleCanvases,
  windowViewType,
}: CanvasLinkParams): string => {
  const objectId = manifestId.split('/').slice(-2)[0]
  const canvasIds = canvases.map((canvas) => canvas.id)
  const visibleCanvasIds = visibleCanvases.map((canvas) => canvas.id)
  const page = calculatePageParamFromCanvases(
    canvasIds,
    visibleCanvasIds,
    windowViewType
  )
  const canvasLink = new URL(
    `/view/${objectId}`,
    `${window.location.protocol}//${window.location.host}`
  )
  if (page) {
    canvasLink.searchParams.append('page', page)
  }
  return canvasLink.toString()
}

/** Get the start canvas index and the view type */
const getStartCanvasIdxAndView = (
  pageSpec: string,
  isWidescreen: boolean
): [number, string] => {
  // By default, start on the first canvas, and pick the view based on the aspect ratio
  if (!pageSpec || pageSpec.length === 0) {
    return [0, isWidescreen ? 'book' : 'single']
  }
  // Otherwise, check if we're supposed to be in single or book view
  // For book view, additionally check if the pages should be shifted, so that
  // verso pages become recto and vice versa
  const commaIdx = pageSpec.indexOf(',')
  if (commaIdx === 0 || commaIdx === pageSpec.length - 1) {
    const pageNum = Number.parseInt(pageSpec.replace(',', ''), 10)
    return [pageNum, 'book']
  } else if (commaIdx > -1) {
    const versoPage = Number.parseInt(pageSpec.split(',')[0], 10)
    return [versoPage, versoPage % 2 === 0 ? 'book' : 'bookShifted']
  }
  return [Number.parseInt(pageSpec), 'single']
}

/** Remove any sequence number from the canvas label (e.g. "(0718)") */
const handleCanvasLabel = ({
  canvasId,
  canvasLabel,
}: CanvasNavigationParams): string => {
  const canvasIdx = Number.parseInt(
    canvasId?.split('/').slice(-1)[0] ?? '0',
    10
  )
  const MDZ_SEQ_NO_PAT = /\((\d{4})\)/
  const seqNo = canvasLabel?.match(MDZ_SEQ_NO_PAT)?.[1]
  if (seqNo !== undefined && Number.parseInt(seqNo, 10) === canvasIdx) {
    return canvasLabel.replace(MDZ_SEQ_NO_PAT, '').trim()
  }
  return canvasLabel
}

const renderViewer = ({
  dfgViewerUrl,
  issueId,
  language,
  lastSearchUrl,
  manifestUrl,
  multId,
  navDate,
  target,
}: RenderViewerParams): void => {
  const isWidescreen = window.matchMedia('(min-aspect-ratio: 4/3)').matches

  // Parse URL params
  const params = Object.fromEntries(new URLSearchParams(location.search))

  const [canvasIndex, view] = getStartCanvasIdxAndView(
    params.page,
    isWidescreen
  )

  const config = {
    id: target,
    language,
    theme: {
      typography: {
        fontFamily: [
          'RobotoDraft',
          'Roboto',
          'Helvetica Neue',
          'Helvetica',
          'Arial',
          'sans-serif',
        ].join(', '),
        h4: {
          marginBottom: '.7em',
        },
        subtitle2: {
          fontWeight: 700,
          lineHeight: 'inherit',
        },
      },
    },
    thumbnailNavigation: {
      defaultPosition: 'far-right',
    },
    translations: {
      de: {
        rights: 'Rechtehinweise',
      },
    },
    window: {
      allowClose: true,
      allowFullscreen: false,
      allowMaximize: false,
      canvasLink: {
        getCanvasLink,
      },
      canvasNavigation: {
        handleCanvasLabel,
      },
      imageToolsEnabled: true,
      imageToolsOpen: false,
      panels: {
        info: true,
        attribution: true,
        canvas: true,
        annotations: true,
        search: true,
      },
      // Show the sidebar always; if not portrait-view
      sideBarOpen: isWidescreen,
      textOverlay: {
        enabled: true,
        visible: params.showText !== undefined,
        // Disable selectability by default on touchscreens, where zooming is performed via pinch&zoom
        selectable: false,
      },
    },
    windows: [
      {
        // This initial window should never be closed
        allowClose: false,
        calendarNavigation: {
          issueId,
          navDate,
          multId,
          lastSearchUrl,
        },
        // The request parameter is 1-based, but the canvasIndex is 0-based
        canvasIndex: canvasIndex - 1,
        defaultSearchQuery: params.q ?? undefined,
        dfgViewerUrl,
        loadedManifest: manifestUrl,
        shiftBookView: view === 'bookShifted',
        sideBarPanel: params.q ? 'search' : 'calendar',
        switchCanvasOnSearch: params.page === undefined,
        view: view.startsWith('book') ? 'book' : view,
      },
    ],
    // Run in multi-window mode
    workspaceControlPanel: {
      enabled: true,
    },
  }
  Mirador.viewer(config, plugins)
}

export default renderViewer
export type { RenderViewerParams }
